import { createFileRoute, useNavigate, useSearch, redirect } from "@tanstack/react-router";
import { dehydrate, DehydratedState, hydrate, useQuery, useQueryClient } from "@tanstack/react-query";
import { queueQueryOptions, queueCallscreeningQueryOptions } from "@/server/queues";
import { CallScreening } from "@/components/call-screening";
import { useEffect } from "react";

export const Route = createFileRoute("/_dashboard/callscreening/queues/$queueId")({
  component: RouteComponent,
  beforeLoad: ({ location, params }) => {
    const search = (location as any).search || {};
    if (!search.tab) {
      throw redirect({
        to: "/callscreening/queues/$queueId",
        params: { queueId: params.queueId },
        replace: true,
        search: { ...search, tab: "rules" },
      });
    }
  },
  loader: async ({ context, params }) => {
    const queryOptions = queueQueryOptions(Number(params.queueId));
    await context.queryClient.prefetchQuery(queryOptions);
    const queue = context.queryClient.getQueryData(queryOptions.queryKey) as { name?: string } | undefined;
    return {
      crumb: queue?.name,
      dehydratedState: dehydrate(context.queryClient) as any,
    };
  },
});

function RouteComponent() {
  const { queueId } = Route.useParams();
  const { dehydratedState } = Route.useLoaderData() as { dehydratedState: DehydratedState };
  const queryClient = useQueryClient();
  hydrate(queryClient, dehydratedState);
  const queueQuery = useQuery(queueQueryOptions(Number(queueId)));
  const queue = queueQuery.data as { name?: string } | undefined;
  const callscreeningQuery = useQuery(queueCallscreeningQueryOptions(Number(queueId)));
  const callscreeningData = callscreeningQuery.data as
    | {
        incoming?: { modify: string; black: string; white: string };
        outgoing?: { modify: string; black: string; white: string };
      }
    | undefined;

  useEffect(() => {
    if (!queueId) return;
    void queueQuery.refetch();
    void callscreeningQuery.refetch();
  }, [queueId, queueQuery.refetch, callscreeningQuery.refetch]);

  return (
    <CallScreening
      resourceId={Number(queueId)}
      callscreeningData={callscreeningData}
      resourceType="queue"
      resource={queue}
      loading={callscreeningQuery.isLoading}
    />
  );
}
